import { getPreferredReviewsSku, getPreferredReviewsStoreKey } from './reviews-config';

/**
 * Product Reviews Share Modal.
 * Handles social sharing functionality for product reviews.
 */

/**
 * Handles social sharing functionality for product reviews.
 */
class ProductReviewsShare {
  constructor() {
    this.init();
  }

  /**
   * Initialises the share functionality.
   */
  init() {
    this.bindEvents();
  }

  /**
   * Binds event listeners for social sharing.
   */
  bindEvents() {
    // Handle social sharing buttons.
    document.addEventListener('click', this.handleClick.bind(this));

    // Handle review form submission.
    document.addEventListener('submit', this.handleFormSubmit.bind(this));

    // Handle rating selection.
    document.addEventListener('change', this.handleRatingChange.bind(this));
  }

  /**
   * Handles click events on social sharing buttons.
   * @param {Event} event - The click event.
   */
  handleClick(event) {
    if (!event.target || !(event.target instanceof Element)) return;

    const socialButton = event.target.closest('.js-share-platform');
    if (socialButton) {
      event.preventDefault();
      this.handleSocialShare(socialButton);
      return;
    }

    const reviewFormToggle = event.target.closest('.js-review-form-toggle');
    if (reviewFormToggle) {
      event.preventDefault();
      this.handleReviewFormToggle(reviewFormToggle);
      return;
    }
  }

  /**
   * Handles social sharing for different platforms.
   * @param {Element} button - The social sharing button element.
   */
  handleSocialShare(button) {
    if (!(button instanceof HTMLElement)) return;
    const platform = button.dataset.sharePlatform;
    const productTitle = this.getProductTitle();
    const productUrl = window.location.href;
    const storeName = this.getStoreName();

    const shareOptions = {
      platform,
      productTitle,
      productUrl,
      storeName
    };

    switch (platform) {
      case 'facebook':
        this.shareOnFacebook(shareOptions);
        break;
      case 'twitter':
        this.shareOnTwitter(shareOptions);
        break;
      case 'email':
        this.shareViaEmail(shareOptions);
        break;
      default:
        console.warn('Unknown sharing platform:', platform);
    }
  }

  /**
   * Gets the product title from the DOM.
   * @returns {string} The product title.
   */
  getProductTitle() {
    const titleElement = document.querySelector('.js-reviews-product-title');
    return titleElement?.textContent || 'Check out this product';
  }

  /**
   * Gets the store name from the DOM.
   * @returns {string} The store name.
   */
  getStoreName() {
    const storeElement = document.querySelector('.js-reviews-store-name');
    return storeElement?.textContent || 'our store';
  }

  /**
   * Shares content on Facebook.
   * @param {Object} options - Sharing options.
   * @param {string} options.productUrl - The product URL to share.
   */
  shareOnFacebook({ productUrl }) {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    this.openShareWindow(shareUrl, 'facebook-share-dialog', 600, 400);
  }

  /**
   * Shares content on Twitter.
   * @param {Object} options - Sharing options.
   * @param {string} options.productTitle - The product title.
   * @param {string} options.productUrl - The product URL to share.
   * @param {string} options.storeName - The store name.
   */
  shareOnTwitter({ productTitle, productUrl, storeName }) {
    const shareText = `Check out ${productTitle} on ${storeName}! ${productUrl}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(productUrl)}`;
    this.openShareWindow(shareUrl, 'twitter-share-dialog', 600, 400);
  }

  /**
   * Shares content via email.
   * @param {Object} options - Sharing options.
   * @param {string} options.productTitle - The product title.
   * @param {string} options.productUrl - The product URL to share.
   * @param {string} options.storeName - The store name.
   */
  shareViaEmail({ productTitle, productUrl, storeName }) {
    const shareText = `Check out ${productTitle} on ${storeName}!`;
    const shareUrl = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n\n${productUrl}`)}`;
    window.location.href = shareUrl;
  }

  /**
   * Opens a share window with specified dimensions.
   * @param {string} url - The URL to open.
   * @param {string} name - The window name.
   * @param {number} width - The window width.
   * @param {number} height - The window height.
   */
  openShareWindow(url, name, width, height) {
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const windowFeatures = [
      'toolbar=no',
      'location=no',
      'directories=no',
      'status=no',
      'menubar=no',
      'scrollbars=no',
      'resizable=no',
      'copyhistory=no',
      `width=${width}`,
      `height=${height}`,
      `top=${top}`,
      `left=${left}`
    ].join(', ');

    window.open(url, name, windowFeatures);
  }

  /**
   * Handles rating selection changes.
   * @param {Event} event - The change event.
   */
  handleRatingChange(event) {
    if (!event.target || !(event.target instanceof Element)) return;

    const ratingRadio = event.target.closest('input[type="radio"][name="rating"]');
    if (!ratingRadio || !(ratingRadio instanceof HTMLInputElement)) return;

    const modal = ratingRadio.closest('.js-product-popup-modal');
    if (!modal) return;

    const ratingValue = ratingRadio.value;

    // Update visual feedback for selected rating.
    this.updateRatingVisualFeedback(modal, ratingValue);

    // Clear any error styling when rating is selected
    const form = modal.querySelector('[data-review-form]');
    if (form) {
      const ratingInput = form.querySelector('.js-reviews-share-rating-input');
      if (ratingInput && ratingInput instanceof HTMLElement) {
        ratingInput.classList.remove('product-reviews-share__rating-input--error');
      }
    }
  }

  /**
   * Updates the visual feedback for the selected rating.
   * @param {Element} modal - The modal element.
   * @param {string} selectedRating - The selected rating value.
   */
  updateRatingVisualFeedback(modal, selectedRating) {
    if (!(modal instanceof HTMLElement)) return;

    const ratingLabels = modal.querySelectorAll('.product-reviews-share__rating-label');

    ratingLabels.forEach((label, index) => {
      if (!(label instanceof HTMLElement)) return;

      const rating = index + 1; // Left to right: first label is 1 star, last is 5 stars
      const emptyStar = label.querySelector('.js-reviews-share-star-empty');
      const filledStar = label.querySelector('.js-reviews-share-star-filled');

      if (rating <= parseInt(selectedRating, 10)) {
        // Selected rating and below: show filled star.
        if (emptyStar && emptyStar instanceof HTMLElement) {
          emptyStar.style.display = 'none';
        }
        if (filledStar && filledStar instanceof HTMLElement) {
          filledStar.style.display = 'block';
        }
      } else {
        // Above selected rating: show empty star.
        if (emptyStar && emptyStar instanceof HTMLElement) {
          emptyStar.style.display = 'block';
        }
        if (filledStar && filledStar instanceof HTMLElement) {
          filledStar.style.display = 'none';
        }
      }
    });
  }

  /**
   * Resets the rating display to show empty stars.
   * @param {Element} modal - The modal element.
   */
  resetRatingDisplay(modal) {
    if (!(modal instanceof HTMLElement)) return;

    const ratingLabels = modal.querySelectorAll('.product-reviews-share__rating-label');

    // Reset all stars to empty state.
    ratingLabels.forEach((label) => {
      if (label instanceof HTMLElement) {
        const emptyStar = label.querySelector('.js-reviews-share-star-empty');
        const filledStar = label.querySelector('.js-reviews-share-star-filled');

        if (emptyStar && emptyStar instanceof HTMLElement) {
          emptyStar.style.display = 'block';
        }
        if (filledStar && filledStar instanceof HTMLElement) {
          filledStar.style.display = 'none';
        }
      }
    });

    // Uncheck all radio buttons.
    const ratingRadios = modal.querySelectorAll('input[type="radio"][name="rating"]');
    ratingRadios.forEach((radio) => {
      if (radio instanceof HTMLInputElement) {
        radio.checked = false;
      }
    });
  }

  /**
   * Handles review form toggle buttons.
   * @param {Element} button - The toggle button element.
   */
  handleReviewFormToggle(button) {
    if (!(button instanceof HTMLElement)) return;

    const action = button.dataset.action;
    const modal = button.closest('.js-product-popup-modal');
    const reviewForm = modal?.querySelector('.js-reviews-share-review-form');
    const actionsSection = modal?.querySelector('.js-reviews-share-actions');

    if (!modal || !reviewForm || !actionsSection) return;

    if (action === 'show-review-form') {
      if (reviewForm instanceof HTMLElement && actionsSection instanceof HTMLElement) {
        reviewForm.style.display = 'block';
        actionsSection.style.display = 'none';

        // Reset rating display to show empty stars.
        this.resetRatingDisplay(modal);
      }
    } else if (action === 'hide-review-form') {
      if (reviewForm instanceof HTMLElement && actionsSection instanceof HTMLElement) {
        reviewForm.style.display = 'none';
        actionsSection.style.display = 'flex';
      }
    }
  }

  /**
   * Handles review form submission.
   * @param {Event} event - The form submit event.
   */
  handleFormSubmit(event) {
    if (!event.target || !(event.target instanceof Element)) return;

    const form = event.target.closest('.js-review-form');
    if (!form) return;

    event.preventDefault();

    // Validate that a rating is selected
    const selectedRating = form.querySelector('input[name="rating"]:checked');
    if (!selectedRating) {
      const modal = form.closest('.js-product-popup-modal');
      if (modal) {
        this.showFormError(modal, 'Please select a rating before submitting your review.');

        // Add error styling to rating input
        const ratingInput = form.querySelector('.js-reviews-share-rating-input');
        if (ratingInput && ratingInput instanceof HTMLElement) {
          ratingInput.classList.add('product-reviews-share__rating-input--error');
        }
      }
      return;
    }

    // Remove error styling if rating is selected
    const ratingInput = form.querySelector('.js-reviews-share-rating-input');
    if (ratingInput && ratingInput instanceof HTMLElement) {
      ratingInput.classList.remove('product-reviews-share__rating-input--error');
    }
    this.submitReview(form);
  }

  /**
   * Submits the review to the Reviews.io API.
   * @param {Element} form - The review form element.
   */
  async submitReview(form) {
    if (!(form instanceof HTMLFormElement)) return;

    const formData = new FormData(form);
    const modal = form.closest('.js-product-popup-modal');
    const submitButton = form.querySelector('button[type="submit"]');

    if (!modal || !submitButton) return;

    // Get product SKU from the page.
    const productSku = getPreferredReviewsSku() || this.getFallbackProductSku();

    if (!productSku) {
      this.showFormError(modal, 'Unable to identify product. Please refresh the page and try again.');
      return;
    }

    // Prepare review data.
    const name = formData.get('name');
    const email = formData.get('email');
    const rating = formData.get('rating');
    const review = formData.get('review');
    const orderId = formData.get('order_id');

    // Validate required fields.
    if (!name || !email || !rating || !review) {
      this.showFormError(modal, 'Please fill in all required fields.');
      return;
    }

    const reviewData = {
      store: getPreferredReviewsStoreKey(),
      sku: productSku || '',
      name: name.toString(),
      email: email.toString(),
      rating: rating.toString(),
      review: review.toString(),
      order_id: orderId ? orderId.toString() : undefined
    };

    try {
      // Disable submit button and show loading state.
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = true;
        submitButton.textContent = 'Submitting...';
      }

      // Submit review to Reviews.io API.
      const response = await fetch('https://api.reviews.io/product/review/new', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reviewData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        this.showFormSuccess(modal, 'Thank you! Your review has been submitted successfully.');
        form.reset();
        // Hide form and show actions after success.
        setTimeout(() => {
          this.hideReviewForm(modal);
        }, 2000);
      } else {
        throw new Error(result.message || 'Review submission failed.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      this.showFormError(modal, 'Sorry, there was an error submitting your review. Please try again later.');
    } finally {
      // Re-enable submit button.
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit Review';
      }
    }
  }

  /**
   * Gets a fallback product SKU from page context when config doesn't provide one.
   * @returns {string|null} The product SKU or null if not found.
   */
  getFallbackProductSku() {
    // Fallback: try to get from product variant.
    const variantSkuElement = document.querySelector('.js-product-sku');
    if (variantSkuElement?.textContent) return variantSkuElement.textContent;

    // Additional fallback: try to get from product handle or ID
    const productHandle = window.location.pathname.split('/').pop();
    if (productHandle && productHandle !== '') {
      return productHandle;
    }

    // Try to get from data attributes
    const productHandleElement = document.querySelector('.js-reviews-product-handle');
    if (productHandleElement?.textContent) {
      return productHandleElement.textContent;
    }

    const productIdElement = document.querySelector('.js-reviews-product-id');
    if (productIdElement?.textContent) {
      return productIdElement.textContent;
    }

    // Try to get from variant SKUs
    const variantSkuElements = document.querySelectorAll('.js-reviews-variant-sku');
    if (variantSkuElements.length > 0) {
      const firstSku = variantSkuElements[0].textContent;
      if (firstSku && firstSku.trim() !== '') {
        return firstSku.trim();
      }
    }

    console.warn('No product SKU found');
    return null;
  }

  /**
   * Shows a form error message.
   * @param {Element} modal - The modal element.
   * @param {string} message - The error message.
   */
  showFormError(modal, message) {
    this.showFormMessage(modal, message, 'error');
  }

  /**
   * Shows a form success message.
   * @param {Element} modal - The modal element.
   * @param {string} message - The success message.
   */
  showFormSuccess(modal, message) {
    this.showFormMessage(modal, message, 'success');
  }

  /**
   * Shows a form message (error or success).
   * @param {Element} modal - The modal element.
   * @param {string} message - The message to display.
   * @param {string} type - The message type ('error' or 'success').
   */
  showFormMessage(modal, message, type) {
    if (!(modal instanceof HTMLElement)) return;

    // Remove existing messages.
    const existingMessage = modal.querySelector('.js-reviews-share-form-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message element.
    const messageElement = document.createElement('div');
    messageElement.className = `product-reviews-share__form-message product-reviews-share__form-message--${type}`;
    messageElement.textContent = message;

    // Insert message after form header.
    const formHeader = modal.querySelector('.js-reviews-share-form-header');
    if (formHeader) {
      formHeader.insertAdjacentElement('afterend', messageElement);
    }

    // Auto-remove message after 5 seconds.
    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.remove();
      }
    }, 5000);
  }

  /**
   * Hides the review form and shows the actions section.
   * @param {Element} modal - The modal element.
   */
  hideReviewForm(modal) {
    if (!(modal instanceof HTMLElement)) return;

    const reviewForm = modal.querySelector('.js-reviews-share-review-form');
    const actionsSection = modal.querySelector('.js-reviews-share-actions');

    if (reviewForm && actionsSection) {
      if (reviewForm instanceof HTMLElement && actionsSection instanceof HTMLElement) {
        reviewForm.style.display = 'none';
        actionsSection.style.display = 'flex';
      }
    }
  }

  /**
   * Cleans up event listeners.
   */
  destroy() {
    document.removeEventListener('click', this.handleClick.bind(this));
  }
}

// Initialise when DOM is ready.
document.addEventListener('DOMContentLoaded', () => {
  new ProductReviewsShare();
});

export default ProductReviewsShare;
